"use client"

import { useEffect, useState } from "react"
import { SearchDrawer } from "./search-drawer"
import { Drawer } from "@medusajs/ui"
import { CartDrawerContent } from "@/modules/cart/components/cart-drawer-content"
import { dispatchCartDrawerClosed } from "@/lib/data/cart-event-bus"
import { useCart } from "@/lib/context/cart-context"
import { captureAnalyticsEvent, syncAnalyticsContext } from "@/lib/analytics/posthog-client"
import ShoppingBag from "@/modules/common/icons/shopping-bag"
import { HttpTypes } from "@medusajs/types"
import { StoreFreeShippingPrice } from "@/types/shipping-option/http"

type SearchBoxProps = {
  customer?: HttpTypes.StoreCustomer | null
  freeShippingPrices?: StoreFreeShippingPrice[]
}

export function SearchBox({ customer = null, freeShippingPrices = [] }: SearchBoxProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isCartOpen, setIsCartOpen] = useState(false)
  const { cart } = useCart()

  useEffect(() => {
    syncAnalyticsContext({ cart, customer })
  }, [cart, customer])

  const handleClose = () => {
    setIsDrawerOpen(false)
  }

  const handleOpen = () => {
    setIsDrawerOpen(true)
  }

  const handleCartOpen = () => {
    setIsCartOpen(true)
    captureAnalyticsEvent("cart_opened", {}, { cart, customer })
  }

  const handleCartClose = () => {
    setIsCartOpen(false)
    dispatchCartDrawerClosed()
  }

  const handleCartOpenChange = (open: boolean) => {
    setIsCartOpen(open)
    if (!open) {
      dispatchCartDrawerClosed()
    }
  }

  // Calculate cart items count
  const items = cart?.items || []
  const totalItems = items?.reduce((acc, item) => acc + item.quantity, 0) || 0

  return (
    <>
      {/* Mobile: Show search and cart icons */}
      <div className="small:hidden flex items-center gap-2 flex-shrink-0">
        {/* Search Icon */}
        <button
          onClick={handleOpen}
          className="flex items-center justify-center p-2 hover:bg-gray-100/10
            rounded-full transition-colors group flex-shrink-0"
          aria-label="Išči v ponudbi"
        >
          <svg
            className="w-5 h-5 text-[#6B8CAE] group-hover:text-[#8BACD4] transition-colors"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </button>

        {/* Cart Icon */}
        <button
          onClick={handleCartOpen}
          className="flex items-center justify-center p-2 hover:bg-gray-100/10
            rounded-full transition-colors group relative flex-shrink-0"
          aria-label={
            totalItems > 0
              ? `Odpri košarico (${totalItems})`
              : "Odpri košarico"
          }
        >
          <ShoppingBag
            size={20}
            fill="currentColor"
            className="text-[#6B8CAE] group-hover:text-[#8BACD4] transition-colors"
          />
          {totalItems > 0 && (
            <div
              className="absolute -top-1 -right-1 bg-gold text-navy text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold"
              aria-hidden="true"
            >
              {totalItems}
            </div>
          )}
        </button>
      </div>

      {/* Desktop: Search bar that opens drawer on click/focus */}
      <button
        type="button"
        onClick={handleOpen}
        className="hidden small:flex items-center gap-2 min-w-[140px] w-36 px-3 py-1 ml-auto
          bg-gray-50/80 hover:bg-gray-100/80 border border-gray-200 rounded-xl
          transition-colors cursor-pointer group text-left"
      >
        <svg
          className="w-4 h-4 flex-shrink-0 text-[#6B8CAE] group-hover:text-[#8BACD4] transition-colors"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <span className="text-sm text-gray-500 group-hover:text-gray-600 truncate">
          Išči v ponudbi...
        </span>
      </button>

      {/* Search Drawer - shared for both mobile and desktop */}
      <SearchDrawer 
        isOpen={isDrawerOpen} 
        onClose={handleClose} 
      />

      {/* Cart Drawer - mobile only */}
      <Drawer open={isCartOpen} onOpenChange={handleCartOpenChange}>
        <Drawer.Content
          className="z-[70] rounded-none m-0 p-0 inset-y-0 sm:right-0"
          overlayProps={{ className: "z-[70]" }}
        >
          <CartDrawerContent
            customer={customer}
            freeShippingPrices={freeShippingPrices}
            onClose={handleCartClose}
          />
        </Drawer.Content>
      </Drawer>
    </>
  )
} 